import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LearningCharts } from "@/components/LearningCharts";
import { Brain, AlertTriangle, Sparkles, Loader2, Eye, Download } from "lucide-react";
import { toast } from "sonner";

export default function LearningMonitorPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [missedOnly, setMissedOnly] = useState("all");
  const [courses, setCourses] = useState([]);
  const [courseFilter, setCourseFilter] = useState("all");
  const [view, setView] = useState(null);
  const [detail, setDetail] = useState(null);
  const [charts, setCharts] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (courseFilter !== "all") params.set("course_id", courseFilter);
    if (missedOnly === "missed") params.set("repeatedly_missed_only", "true");
    const r = await api.get(`/admin/learning-monitor?${params}`);
    setRows(r.data);
  };
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data || [])); }, []);
  useEffect(() => { load(); }, [courseFilter, missedOnly]);

  const openView = async (r) => {
    setView(r); setDetail(null); setCharts(null);
    const [d, c] = await Promise.all([
      api.get(`/admin/learning-monitor/${r.student_id}`),
      api.get(`/admin/learning-monitor/${r.student_id}/charts`).catch(() => ({ data: null })),
    ]);
    setDetail(d.data);
    setCharts(c.data);
  };

  const generateInsight = async () => {
    if (!view) return;
    setGenLoading(true);
    try {
      await api.post(`/admin/learning-monitor/${view.student_id}/generate-insight`);
      toast.success("AI insight generated");
      const d = await api.get(`/admin/learning-monitor/${view.student_id}`);
      setDetail(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate");
    } finally { setGenLoading(false); }
  };

  const exportReport = async (studentId, fullName) => {
    try {
      const token = localStorage.getItem("pm_token");
      const resp = await fetch(`${API}/admin/learning-report/${studentId}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `learning_report_${(fullName || "student").replace(/\s+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not download report");
    }
  };

  const filtered = rows.filter((r) => !search || `${r.full_name} ${r.email}`.toLowerCase().includes(search.toLowerCase()));

  const accColor = (n) => n >= 80 ? "text-green-700" : n >= 60 ? "text-amber-700" : "text-red-700";

  return (
    <div className="space-y-6" data-testid="learning-monitor-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2"><Brain className="w-3.5 h-3.5" /> AI Learning Monitor</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Student Performance & Weak Areas</h1>
        <p className="text-slate-600 mt-1">Identify students who need intervention, repeatedly missed questions, and weak subjects.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search students…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Course" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Courses</SelectItem>
            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={missedOnly} onValueChange={setMissedOnly}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Students</SelectItem>
            <SelectItem value="missed">Has Repeatedly Missed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200 hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Total Attempts</TableHead>
              <TableHead>Accuracy</TableHead>
              <TableHead>Repeatedly Missed</TableHead>
              <TableHead>Weak Subject</TableHead>
              <TableHead>Weak Module</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No data yet — students need to answer questions first.</TableCell></TableRow> :
              filtered.map((r) => (
                <TableRow key={r.student_id} className="even:bg-slate-50">
                  <TableCell><div className="font-medium">{r.full_name}</div><div className="text-xs text-slate-500">{r.email}</div></TableCell>
                  <TableCell>{r.course_name || "—"}</TableCell>
                  <TableCell>{r.total_attempts}</TableCell>
                  <TableCell><span className={`font-semibold ${accColor(r.accuracy)}`}>{r.accuracy}%</span></TableCell>
                  <TableCell>{r.repeatedly_missed > 0 ? <Badge className="bg-amber-100 text-amber-800">{r.repeatedly_missed}</Badge> : <span className="text-slate-400">0</span>}</TableCell>
                  <TableCell className="text-sm">{r.weakest_subject ? <span>{r.weakest_subject} <span className="text-slate-500">({r.weakest_subject_accuracy}%)</span></span> : "—"}</TableCell>
                  <TableCell className="text-sm">{r.weakest_module || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openView(r)} data-testid={`view-${r.student_id}`}><Eye className="w-4 h-4 mr-1" />View</Button>
                      <Button size="sm" variant="outline" onClick={() => exportReport(r.student_id, r.full_name)} data-testid={`export-${r.student_id}`}><Download className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <div className="lg:hidden grid gap-3">
        {filtered.map((r) => (
          <Card key={r.student_id} className="p-4 border-slate-200">
            <div className="font-semibold">{r.full_name}</div>
            <div className="text-xs text-slate-500">{r.email}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">{r.total_attempts} answered</Badge>
              <Badge className={accColor(r.accuracy) + " bg-white border"}>{r.accuracy}%</Badge>
              {r.repeatedly_missed > 0 && <Badge className="bg-amber-100 text-amber-800">{r.repeatedly_missed} missed</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-2">Weakest: {r.weakest_subject || "—"}</div>
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => openView(r)}><Eye className="w-4 h-4 mr-1" />View</Button>
          </Card>
        ))}
      </div>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{view?.full_name}</DialogTitle>
          </DialogHeader>
          {!detail ? <div className="text-center text-slate-500 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <Card className="p-3"><div className="text-xs text-slate-500">Accuracy</div><div className={`text-2xl font-display font-bold ${accColor(detail.accuracy)}`}>{detail.accuracy}%</div></Card>
                <Card className="p-3"><div className="text-xs text-slate-500">Attempts</div><div className="text-2xl font-display font-bold">{detail.total_attempts}</div></Card>
                <Card className="p-3"><div className="text-xs text-slate-500">Wrong</div><div className="text-2xl font-display font-bold text-red-700">{detail.wrong_attempts}</div></Card>
              </div>

              {detail.weak.weakest_subject && (
                <div className="text-sm">
                  <div className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-1">Weakest Subject</div>
                  <div>{detail.weak.weakest_subject.name} <Badge variant="outline">{detail.weak.weakest_subject.accuracy}%</Badge></div>
                </div>
              )}
              {detail.weak.weakest_module && (
                <div className="text-sm">
                  <div className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-1">Weakest Module</div>
                  <div>{detail.weak.weakest_module.name} <Badge variant="outline">{detail.weak.weakest_module.accuracy}%</Badge></div>
                </div>
              )}

              {charts && (
                <div data-testid="admin-charts-section">
                  <div className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-2">Learning Analytics</div>
                  <LearningCharts data={charts} testIdPrefix="admin-" />
                </div>
              )}

              {detail.repeatedly_missed.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Repeatedly Missed</div>
                  <div className="border border-slate-200 rounded-md max-h-60 overflow-y-auto divide-y">
                    {detail.repeatedly_missed.slice(0, 10).map((m) => (
                      <div key={m.id} className="p-2 text-sm">
                        <div className="line-clamp-2 text-slate-800">{m.question_text}</div>
                        <div className="text-xs text-slate-500 mt-1">Wrong: {m.wrong_attempts} · Acc: {m.accuracy_percentage}% · Mastery L{m.mastery_level}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.ai_insight ? (
                <div className="ai-card p-4">
                  <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide flex items-center gap-2 mb-1"><Sparkles className="w-3.5 h-3.5" /> Admin Observation</div>
                  <p className="text-sm text-slate-800 leading-relaxed">{detail.ai_insight.insight_text}</p>
                  <div className="text-xs text-slate-500 mt-2">Generated {detail.ai_insight.generated_at?.slice(0, 10)}</div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 text-center p-4 border border-dashed border-slate-200 rounded-md">No AI insight yet — click Generate below.</div>
              )}

              <div className="text-xs text-slate-500">Smart Review sessions: {detail.sessions.length}</div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setView(null)}>Close</Button>
            <Button variant="outline" onClick={() => view && exportReport(view.student_id, view.full_name)} disabled={!detail} data-testid="dialog-export-btn">
              <Download className="w-4 h-4 mr-1" /> Export Report
            </Button>
            <Button onClick={generateInsight} disabled={genLoading || !detail} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="generate-insight-btn">
              {genLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate AI Insight</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
